import { Component } from '@theme/component';
import '@theme/dialog';

class CorkMartLaserPanel extends Component {
  #controller; #opening = false; #fromHistory = false; #invalidTarget; #closeFocus; #completed = false; #thumbnailUrl;
  connectedCallback() { super.connectedCallback(); this.connect(); }
  updatedCallback() { super.updatedCallback(); this.connect(); }
  connect() {
    this.#controller?.abort(); this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.dialogHost = this.querySelector('[data-panel-dialog-host]');
    this.dialog = this.querySelector('dialog'); this.launcher = this.querySelector('[data-open-personaliser]');
    if (!this.dialog || !this.launcher || typeof this.dialog.showModal !== 'function') return;
    if (!this.hasAttribute('data-ready')) this.dialog.close();
    this.addEventListener('click', this.onClick, options);
    this.addEventListener('keydown', this.onTabBoundary, options);
    this.addEventListener('input', this.onDirty, options);
    this.addEventListener('change', this.onDirty, options);
    this.dialogHost.addEventListener('dialog:open', this.onOpen, options);
    this.dialogHost.addEventListener('dialog:close', this.onClose, options);
    window.addEventListener('popstate', this.onHistory, options);
    window.addEventListener('pageshow', this.onHistory, options);
    this.setAttribute('data-ready', ''); this.querySelector('[data-panel-launcher]').hidden = false;
    if (!this.#completed) this.setCompletion(false);
    for (const control of this.querySelectorAll('[data-panel-header],[data-panel-footer]')) control.hidden = false;
    this.onHistory();
  }
  disconnectedCallback() {
    super.disconnectedCallback(); this.#controller?.abort();
    document.documentElement.removeAttribute('data-cm-laser-panel-open');
    this.releaseThumbnail();
  }
  releaseThumbnail() { if (this.#thumbnailUrl) URL.revokeObjectURL(this.#thumbnailUrl); this.#thumbnailUrl = undefined; }
  onDirty = () => { if (this.#completed) this.setCompletion(false); };
  setCompletion(complete) {
    this.#completed = complete; this.toggleAttribute('data-completed', complete); this.releaseThumbnail();
    const image = this.querySelector('[data-completed-preview]');
    const fileMark = this.querySelector('[data-completed-file]');
    const summary = this.querySelector('[data-completed-summary]');
    image.hidden = true; image.removeAttribute('src'); fileMark.hidden = true;
    this.launcher.classList.toggle('button', !complete); this.launcher.classList.toggle('button-secondary', complete);
    this.launcher.textContent = complete ? this.launcher.dataset.labelEdit : this.launcher.dataset.labelOpen;
    if (!complete) { summary.textContent = this.launcher.dataset.pending; return; }
    const file = this.querySelector('input[type="file"]')?.files?.[0];
    const creator = this.querySelector('cm-laser-creator');
    const creating = creator?.querySelector('[data-artwork-mode]:checked')?.value === 'create';
    const artwork = creator?.artwork;
    if (creating && !file && !artwork) { this.setCompletion(false); return; }
    summary.textContent = this.querySelector('cm-laser-faces')?.summary || (file ? file.name : creating ? artwork.summary : '');
    if (file?.type.startsWith('image/')) this.#thumbnailUrl = URL.createObjectURL(file);
    else if (!file && creating) {
      this.#thumbnailUrl = URL.createObjectURL(artwork.file);
      image.dataset.artworkRevision = String(artwork.revision);
    }
    if (this.#thumbnailUrl) { image.src = this.#thumbnailUrl; image.hidden = false; }
    else fileMark.hidden = false;
  }
  get ownsEntry() { return history.state?.cmLaserPanel?.id === this.dialog.id && location.hash === '#cm-laser-personalise'; }
  open(fromHistory = false) {
    if (this.dialog.open || this.#opening) { this.focusInvalid(); return; }
    this.#opening = true; this.#fromHistory = false;
    if (this.dataset.editor !== 'true' && !fromHistory && !this.ownsEntry) {
      const url = new URL(location.href);
      const previousHash = url.hash; url.hash = 'cm-laser-personalise';
      const variantId = this.closest('form')?.querySelector('input[name="id"]')?.value;
      history.pushState({ ...history.state, cmLaserPanel: { id: this.dialog.id, previousHash, variantId } }, '', url);
    }
    this.dialogHost.showDialog();
  }
  revealError(field) { this.#invalidTarget = field; this.open(); }
  focusInvalid() {
    if (!this.dialog.open || !this.#invalidTarget?.isConnected) return;
    this.#invalidTarget.focus({ preventScroll: true });
    this.#invalidTarget.scrollIntoView({ block: 'center', behavior: 'instant' });
    this.#invalidTarget = undefined;
  }
  onOpen = () => {
    if (this.dataset.editor !== 'true' && !this.ownsEntry) { this.#opening = false; this.#fromHistory = true; this.dialogHost.closeDialog(); return; }
    this.#opening = false; this.launcher.setAttribute('aria-expanded', 'true');
    const faces = this.querySelector('cm-laser-faces'); if (faces?.refresh) faces.refresh(); else this.querySelector('cm-laser-creator')?.syncGallery();
    document.documentElement.setAttribute('data-cm-laser-panel-open', 'true');
    this.querySelector('[data-panel-title]')?.focus({ preventScroll: true }); this.focusInvalid();
  };
  onClose = () => {
    this.#opening = false; this.launcher.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('data-cm-laser-panel-open');
    const faces = this.querySelector('cm-laser-faces');
    if (faces?.refresh) faces.refresh(); else this.querySelector('cm-laser-creator')?.refreshPreview();
    if (!this.#fromHistory && this.ownsEntry) history.back();
    this.#fromHistory = false;
    const target = this.#closeFocus ?? this.launcher; this.#closeFocus = undefined;
    target?.focus({ preventScroll: true });
  };
  onHistory = () => {
    if (this.dataset.editor === 'true') return;
    if (this.ownsEntry) {
      const variantId = this.closest('form')?.querySelector('input[name="id"]')?.value;
      if (variantId && history.state.cmLaserPanel.variantId !== variantId) {
        const url = new URL(location.href); url.searchParams.set('variant', variantId);
        history.replaceState({ ...history.state, cmLaserPanel: { ...history.state.cmLaserPanel, variantId } }, '', url);
      }
      this.open(true);
    }
    else if (this.dialog.open) { this.#fromHistory = true; this.dialogHost.closeDialog(); }
  };
  onTabBoundary = (event) => {
    if (event.key !== 'Tab' || !this.dialog.open) return;
    const first = this.querySelector('[data-panel-close]');
    const last = this.querySelector('[data-panel-done]');
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-remove-file]')) { if (this.#completed) this.setCompletion(false); return; }
    if (event.target.closest('[data-open-personaliser]')) { this.open(); return; }
    if (event.target.closest('[data-panel-close]')) { this.dialogHost.closeDialog(); return; }
    if (!event.target.closest('[data-panel-done]')) return;
    const fields = [...this.dialog.querySelectorAll('input,textarea,select')];
    const invalid = fields.find((field) => field.willValidate && !field.checkValidity());
    if (invalid) { this.revealError(invalid); return; }
    this.setCompletion(true);
    this.#closeFocus = this.closest('form')?.querySelector('button[type="submit"]');
    this.dialogHost.closeDialog();
  };
}
if (!customElements.get('cm-laser-panel')) customElements.define('cm-laser-panel', CorkMartLaserPanel);
